import { Icon } from "~/common/components/icon/Icon";
import classes from "./RecentSearches.module.less";
import { IconType } from "~/common/components/icon/IconType";
import { IconButton } from "~/common/mui";
import { Link } from "~/common/components/Link";
import { useIsMinDesktopSize, useIsMinTabletSize } from "~/common/hooks/useMediaQuery";
import { useLockBodyScroll } from "~/common/hooks/useLockBodyScroll";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { useEffect, useRef, useState } from "react";
import { resolveApiUrl } from "~/utils/url/urlResolver";
import { getCsrfValue } from "~/common/utils/ApiHelper";
import { getOS } from "~/common/utils/browserChecks";

interface RecentSearchesProps {
  searchData: {
    recentSearches?: SearchEntry[];
    popularSearches?: SearchEntry[];
    state: "loading" | "loaded" | "error";
  };
  removeEntry: (term: string) => void;
  hide: () => void;
  className?: string | false;
}

export type SearchEntry = {
  term: string;
  url: string;
};

const MAX_ITEMS = 5;
const MAX_ITEMS_MOBILE = 3;

const RecentSearches = ({ searchData, removeEntry, hide, className }: RecentSearchesProps) => {
  const { t } = useTranslation();
  const isDesktop = useIsMinDesktopSize();
  const isTablet = useIsMinTabletSize();
  if (!isDesktop) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useLockBodyScroll();
  }

  const [shownRecentSearches, setShownRecentSearches] = useState(searchData.recentSearches);

  const setSlicedRecentSearches = () =>
    setShownRecentSearches(searchData.recentSearches?.slice(0, !isDesktop && !isTablet ? MAX_ITEMS_MOBILE : MAX_ITEMS));

  useEffect(() => setSlicedRecentSearches(), [isTablet, searchData.state]);

  const removeRecentSearchItem = (term: string) => {
    deleteRecentSearchEntry(term);
    removeEntry(term);
    setShownRecentSearches((prevState) => prevState?.filter((entry) => entry.term !== term));
  };

  const os = getOS();
  const isMobileUA = os === "Android" || os === "iOS";

  const refList = useRef<HTMLAnchorElement[]>([]);
  const [focusedIndex, setFocusedIndex] = useState(-1);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "ArrowDown") {
        event.preventDefault();
        setFocusedIndex((prevIndex) => (prevIndex + 1) % refList.current.length);
      } else if (event.key === "ArrowUp") {
        event.preventDefault();
        setFocusedIndex((prevIndex) => (prevIndex - 1 + refList.current.length) % refList.current.length);
      } else if (event.key === "Escape") {
        event.preventDefault();
        hide();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [refList]);

  useEffect(() => {
    if (focusedIndex !== -1) {
      refList.current[focusedIndex].focus();
    }
  }, [focusedIndex]);

  return (
    <div className={classNames(className)}>
      {searchData.recentSearches && searchData.recentSearches.length > 0 && (
        <>
          <div className={classes.header}>{t("recentSearches.recent")}</div>
          {shownRecentSearches?.map((item, index) => (
            <div
              tracking-aux-sch-recent-name={item.term}
              key={index}
              className={classNames(classes.searchItemContainer, !isMobileUA && classes.hoverEffects)}
            >
              <Link
                ref={(el) => {
                  if (el && !refList.current.includes(el)) {
                    refList.current.push(el);
                  }
                }}
                className={classes.searchItemLink}
                href={item.url}
              >
                <div className={classes.searchItem}>
                  <Icon type={IconType.History} />
                  <span>{item.term}</span>
                </div>
              </Link>

              <IconButton
                onClick={(e) => {
                  removeRecentSearchItem(item.term);
                  e.currentTarget.blur();
                }}
                className={classNames(classes.removeButton, isMobileUA && classes.disableHover)}
                color="primary"
              >
                <Icon type={IconType.Close} />
              </IconButton>
            </div>
          ))}
        </>
      )}
      {searchData.popularSearches && searchData.popularSearches.length > 0 && (
        <>
          <div className={classes.header}>{t("recentSearches.popular")}</div>
          {searchData.popularSearches?.slice(0, MAX_ITEMS).map((item, index) => (
            <div
              tracking-aux-sch-popular-name={item.term}
              key={index}
              className={classNames(classes.searchItemContainer, !isMobileUA && classes.hoverEffects)}
            >
              <Link
                ref={(el) => {
                  if (el && !refList.current.includes(el)) {
                    refList.current.push(el);
                  }
                }}
                className={classes.searchItemLink}
                href={item.url}
              >
                <div className={classes.searchItem}>
                  <Icon type={IconType.Trending} />
                  <span>{item.term}</span>
                </div>
              </Link>
            </div>
          ))}
        </>
      )}
    </div>
  );
};

export default RecentSearches;

const deleteRecentSearchEntry = async (term: string) => {
  return await fetch(resolveApiUrl(`/search/remove-search-term?term=${term}`), {
    method: "GET",
    cache: "no-cache",
    credentials: "same-origin",
    mode: "cors",
    referrer: "no-referrer",
    headers: {
      "content-type": "application/json",
      CSRFToken: getCsrfValue(),
    },
  });
};
